import { type NotificationType } from "@/hooks/use-notifications";

/**
 * Varsler Fase 2: katalog over alle varseltyper med tier (gruppe i UI) og
 * default-nivå. Single source of truth for Innstillinger-/Varsler-UI og for
 * defaults. Speiler DB-funksjonen `notif_default_level` (samme defaults).
 *
 * Tier styrer hvilke brukere som ser typen i Varsler-siden:
 * - personal: alle brukere
 * - project:  alle utenom `external`
 * - leadership: kun admin/superadmin
 */
export type NotificationTier = "personal" | "project" | "leadership";

export type NotificationLevel = "off" | "in_app" | "email";

export interface NotificationTypeMeta {
  tier: NotificationTier;
  defaultLevel: NotificationLevel;
  label: string;
}

/**
 * Uttømmende katalog. `Record<NotificationType, …>` gjør at tsc krever en
 * entry for hver type i unionen (inkl. `feedback_resolved`).
 */
export const NOTIFICATION_TYPES: Record<NotificationType, NotificationTypeMeta> = {
  task_assigned: { tier: "personal", defaultLevel: "in_app", label: "Oppgave tildelt" },
  task_approved: { tier: "personal", defaultLevel: "in_app", label: "Oppgave godkjent" },
  task_rejected: { tier: "personal", defaultLevel: "in_app", label: "Oppgave avvist" },
  task_comment_added: { tier: "personal", defaultLevel: "in_app", label: "Ny kommentar på oppgave" },
  timebank_approved: { tier: "personal", defaultLevel: "in_app", label: "Timebank godkjent" },
  timebank_rejected: { tier: "personal", defaultLevel: "in_app", label: "Timebank avvist" },
  timebank_cancelled: { tier: "personal", defaultLevel: "in_app", label: "Timebank kansellert" },
  feedback_resolved: { tier: "personal", defaultLevel: "in_app", label: "Tilbakemelding løst" },
  task_pending_approval: { tier: "project", defaultLevel: "in_app", label: "Oppgave til godkjenning" },
  project_update: { tier: "project", defaultLevel: "in_app", label: "Prosjektoppdatering" },
  voucher_pending: { tier: "project", defaultLevel: "in_app", label: "Bilag til godkjenning" },
  timebank_requested: { tier: "leadership", defaultLevel: "in_app", label: "Timebank-forespørsel" },
  absence_registered: { tier: "leadership", defaultLevel: "in_app", label: "Fravær registrert" },
  // VIB-2092: DL-oppfølging, ikke noe den enkelte ansatte skal se i lista.
  extra_ready_for_payment: {
    tier: "leadership",
    defaultLevel: "in_app",
    label: "Statister klare for utbetaling",
  },
  // VIB-1296: default 'email' (avvik fra ellers-universell 'in_app'-default,
  // Erik 15.07-avklaring) — poenget er å nå folk som ikke er inne i appen.
  // DB-funksjonen notif_default_level() speiler dette unntaket eksplisitt
  // for nettopp denne typen (se migrasjon), ellers 'in_app' for alle andre.
  missing_time_entries: { tier: "personal", defaultLevel: "email", label: "Manglende timeføring" },
  offer_expiring: { tier: "personal", defaultLevel: "in_app", label: "Tilbud utløper snart" },
  // CRM-2.6: alle in_app som notif_default_level() (ingen e-post uten opt-in).
  crm_reply_received: { tier: "personal", defaultLevel: "in_app", label: "Svar mottatt på e-post" },
  crm_meeting_booked: { tier: "personal", defaultLevel: "in_app", label: "Møte booket på deg" },
  crm_summary_ready: { tier: "personal", defaultLevel: "in_app", label: "Møtereferat klart" },
  crm_signal_claimed: { tier: "personal", defaultLevel: "in_app", label: "Signal tatt på din kunde" },
  crm_follow_ups_today: { tier: "personal", defaultLevel: "in_app", label: "Dagens oppfølginger" },
  // VIB-2925: @-nevning i frilanserbanken.
  freelancer_mention: { tier: "personal", defaultLevel: "in_app", label: "Nevnt på en frilanser" },
  // VIB-2933: utstyrsvarsler, personlige fordi alle kan være ansvarlig for en booking.
  equipment_damage: { tier: "personal", defaultLevel: "in_app", label: "Skade meldt på utstyr" },
  equipment_booking_alert: { tier: "personal", defaultLevel: "in_app", label: "Booket utstyr på service eller dobbeltbooket" },
  // VIB-3025: @-nevning i Chat-rommet. Nevning i en prosjektkanal er project_update.
  chat_mention: { tier: "personal", defaultLevel: "in_app", label: "Nevnt i Chat" },
};

/** Chat-typene vises i Varsler-innstillingene først når Chat-rommet er rullet ut (feature_chat_rom). */
export function isChatNotificationType(type: string): boolean {
  return type.startsWith("chat_");
}

/** Utstyrstypene vises i Varsler-innstillingene først når utstyr er rullet ut (feature_equipment). */
export function isEquipmentNotificationType(type: string): boolean {
  return type.startsWith("equipment_");
}

/** CRM-typene vises i Varsler-innstillingene først når flaten er rullet ut (feature_crm_ko_pipe). */
export function isCrmNotificationType(type: string): boolean {
  return type.startsWith("crm_");
}
